// Header.jsx - FIXED VERSION with proper search and auth support
import React, { useState, useRef, useEffect } from 'react';
import { Heart, User, LogOut, Menu, Search, X, Home, Sparkles, ArrowLeft } from 'lucide-react';
import Logo from './Logo';
import API_URL from '../config/api';

const Header = ({ user, onLogout, currentScreen, onNavigate, onSearch }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchCacheRef = useRef(new Map());
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isSearchExpanded]);

  useEffect(() => {
    setShowMobileMenu(false);
  }, [currentScreen]);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
        if (!searchQuery.trim()) {
          setIsSearchExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchQuery]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsSearchExpanded(false);
        setShowSearchResults(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      if (onSearch) {
        onSearch('', []);
      }
      return;
    }

    const cached = searchCacheRef.current.get(query.toLowerCase());
    if (cached) {
      setSearchResults(cached.results || []);
      setShowSearchResults(true);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      
      abortControllerRef.current = new AbortController();
      
      try {
        const response = await fetch(
          `${API_URL}/api/search?query=${encodeURIComponent(query)}`,
          { signal: abortControllerRef.current.signal }
        );
        
        if (!response.ok) throw new Error('Search failed');
        
        const data = await response.json();
        
        const results = data.results || [];
        
        searchCacheRef.current.set(query.toLowerCase(), {
          results: results
        });
        
        if (searchCacheRef.current.size > 50) {
          const firstKey = searchCacheRef.current.keys().next().value;
          searchCacheRef.current.delete(firstKey);
        }
        
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
        }
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && onSearch) {
      setShowSearchResults(false);
      setIsSearchExpanded(false);
      
      // Call the onSearch callback with query and results
      onSearch(searchQuery, searchResults);
    }
  };

  const handleProductClick = (product) => {
    setShowSearchResults(false);
    setIsSearchExpanded(false);
    
    // Navigate with this specific product highlighted
    if (onSearch) {
      onSearch('', [product]);
    }
    
    setSearchQuery('');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    if (onSearch) {
      onSearch('', []);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    searchInputRef.current?.focus();
  };

  const closeSearch = () => {
    setIsSearchExpanded(false);
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);
    if (onSearch) {
      onSearch('', []);
    }
  };

  const navItems = [
    { id: 'home', label: 'Home', icon: <Home className="w-5 h-5" /> },
    { id: 'aiFinder', label: 'AI Finder', icon: <Sparkles className="w-5 h-5" /> },
    ...(user ? [
      { id: 'wishlist', label: 'Wishlist', icon: <Heart className="w-5 h-5" /> },
    ] : [])
  ];

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#FBFCF6] border-b border-slate-200 shadow-sm">
        <div className="max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
            <div 
              onClick={() => onNavigate('home')} 
              className={`cursor-pointer flex-shrink-0 transition-opacity ${isSearchExpanded ? 'hidden sm:block' : ''}`}
            >
              <Logo />
            </div>

            {isSearchExpanded && (
              <button
                onClick={closeSearch}
                className="sm:hidden p-2 -ml-1 text-slate-600"
                type="button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}

            <nav className={`hidden md:flex items-center gap-1 lg:gap-2 transition-opacity ${isSearchExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  type="button"
                  className={`flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    currentScreen === item.id
                      ? 'text-[#06402B] bg-[#06402B]/5'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-[#06402B]'
                  }`}
                >
                  {item.icon}
                  <span className="hidden lg:inline">{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <div 
                ref={searchRef} 
                className={`relative transition-all duration-300 ${
                  isSearchExpanded 
                    ? 'flex-1 sm:w-80 md:w-96 lg:w-[500px]' 
                    : 'w-auto'
                }`}
              >
                {!isSearchExpanded ? (
                  <button
                    onClick={() => setIsSearchExpanded(true)}
                    type="button"
                    className="p-2 sm:p-2.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                ) : (
                  <div className="w-full">
                    <form onSubmit={handleSearchSubmit} className="relative">
                      <div className="flex items-center bg-slate-100 rounded-xl transition-all">
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          onFocus={() => searchQuery && setShowSearchResults(true)}
                          placeholder="Search for gifts..."
                          className="flex-1 pl-4 pr-2 py-2.5 sm:py-3 text-sm sm:text-base outline-none bg-transparent rounded-l-xl focus:ring-0 focus:outline-none border-none"
                          autoComplete="off"
                        />
                        <div className="flex items-center gap-1 pr-1.5">
                          {isSearching && (
                            <div className="w-5 h-5 border-2 border-[#06402B] border-t-transparent rounded-full animate-spin" />
                          )}
                          {searchQuery && !isSearching && (
                            <button
                              type="button"
                              onClick={clearSearch}
                              className="p-1.5 hover:bg-slate-200 rounded-full transition-colors"
                            >
                              <X className="w-4 h-4 text-slate-400" />
                            </button>
                          )}
                          <button
                            type="submit"
                            className="p-2.5 bg-[#06402B] text-white rounded-lg hover:bg-[#053527] transition-colors"
                          >
                            <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      </div>
                    </form>

                    {showSearchResults && searchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-[70vh] overflow-y-auto z-50">
                        <div>
                          <div className="px-4 py-2 bg-slate-50">
                            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide">
                              {searchResults.length} Products Found
                            </p>
                          </div>
                          {searchResults.slice(0, 5).map((product) => (
                            <button
                              key={product.asin}
                              onClick={() => handleProductClick(product)}
                              type="button"
                              className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center gap-3"
                            >
                              <img
                                src={product.image_urls?.[0]}
                                alt={product.title}
                                className="w-10 h-10 sm:w-12 sm:h-12 object-contain bg-slate-100 rounded-lg flex-shrink-0"
                                loading="lazy"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-slate-800 line-clamp-1">
                                  {product.short_title || product.title}
                                </p>
                                {product.main_category && (
                                  <p className="text-[10px] sm:text-xs text-slate-500">{product.main_category}</p>
                                )}
                              </div>
                            </button>
                          ))}
                          {searchResults.length > 5 && (
                            <button
                              onClick={handleSearchSubmit}
                              type="button"
                              className="w-full px-4 py-3 text-xs sm:text-sm text-[#06402B] font-semibold hover:bg-slate-50 transition-colors border-t border-slate-100"
                            >
                              View all {searchResults.length} results →
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Guest Mode - Sign In/Register Buttons */}
              {!user && (
                <div className={`hidden md:flex items-center gap-2 ${isSearchExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                  <button
                    onClick={() => onNavigate('login')}
                    type="button"
                    className="px-3 lg:px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg font-medium transition-colors text-sm"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => onNavigate('register')}
                    type="button"
                    className="px-3 lg:px-4 py-2 bg-[#06402B] text-white rounded-lg hover:bg-[#053527] font-medium transition-colors text-sm shadow-sm"
                  >
                    Register
                  </button>
                </div>
              )}

              {/* Logged In User - Profile Dropdown */}
              {user && (
                <div className={`relative hidden md:block ${isSearchExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} ref={menuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    type="button"
                    className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#06402B] text-white flex items-center justify-center text-sm font-semibold">
                      {user?.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {user?.user_metadata?.full_name || 'User'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                      </div>

                      <button
                        onClick={() => {
                          onNavigate('profile');
                          setShowUserMenu(false);
                        }}
                        type="button"
                        className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-center gap-3 text-sm"
                      >
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-700">Profile</span>
                      </button>

                      <div className="border-t border-slate-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            onLogout();
                            setShowUserMenu(false);
                          }}
                          type="button"
                          className="w-full px-4 py-2.5 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-sm text-red-600"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => setShowMobileMenu(true)}
                type="button"
                className={`md:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors ${isSearchExpanded ? 'hidden' : ''}`}
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              </button>

              {isSearchExpanded && (
                <button
                  onClick={closeSearch}
                  type="button"
                  className="hidden sm:flex md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMobileMenu(false)}
          />
          
          <div className="absolute right-0 top-0 bottom-0 w-[280px] sm:w-[320px] bg-white shadow-xl animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#06402B] text-white flex items-center justify-center font-semibold">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">
                      {user?.user_metadata?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-semibold">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Guest</p>
                    <p className="text-xs text-slate-500">Not signed in</p>
                  </div>
                </div>
              )}
              <button
                onClick={() => setShowMobileMenu(false)}
                type="button"
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="p-3">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    onNavigate(item.id);
                    setShowMobileMenu(false);
                  }}
                  type="button"
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors mb-1 ${
                    currentScreen === item.id
                      ? 'bg-[#06402B]/10 text-[#06402B]'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="mx-4 border-t border-slate-100" />

            <div className="p-3">
              {user ? (
                <>
                  <button
                    onClick={() => {
                      onNavigate('profile');
                      setShowMobileMenu(false);
                    }}
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-slate-700 hover:bg-slate-50 rounded-xl transition-colors mb-1"
                  >
                    <User className="w-5 h-5" />
                    <span className="font-medium">Profile</span>
                  </button>
                  <button
                    onClick={() => {
                      onLogout();
                      setShowMobileMenu(false);
                    }}
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      onNavigate('login');
                      setShowMobileMenu(false);
                    }}
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-[#06402B] text-white hover:bg-[#053527] rounded-xl transition-colors mb-2"
                  >
                    <span className="font-semibold">Sign In</span>
                  </button>
                  <button
                    onClick={() => {
                      onNavigate('register');
                      setShowMobileMenu(false);
                    }}
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3.5 border-2 border-[#06402B] text-[#06402B] hover:bg-[#06402B]/5 rounded-xl transition-colors"
                  >
                    <span className="font-semibold">Register</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isSearchExpanded && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 sm:hidden"
          onClick={closeSearch}
        />
      )}
    </>
  );
};

export default Header;